import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { STATUS_COLOR_MAP, STATUS_ICONS, COLOR_CHOICES, ICON_CHOICES, invalidateStatusCache } from '@/lib/taskStatuses';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Helper function to safely parse JSON response.
// IMPORTANT: do NOT use response.clone() — it can throw "Response body is already used"
// in some browser / proxy combinations. Read body as text ONCE and try to parse.
const safeJsonParse = async (response) => {
  try {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return { detail: text };
    }
  } catch {
    return { detail: 'Failed to parse response' };
  }
};

import { 
  Plus, 
  Pencil, 
  Trash2, 
  Mail,
  CheckCircle,
  XCircle,
  ExternalLink,
  Package,
  Upload,
  Download,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  AlertCircle,
  Layers,
  Image,
  Link,
  Globe,
  RefreshCw,
  Dna,
  Edit
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { UsageAnalyticsTab } from '@/components/usage/UsageAnalyticsTab';
import { MetaAdAccountsCard } from '@/components/MetaAdAccountsCard';
import { EanOwnersTab } from '@/components/EanOwnersTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { } = useAuth();
  const [searchParams] = useSearchParams();
  const [settingsTab, setSettingsTab] = useState(searchParams.get('tab') || 'brands');
  const [brands, setBrands] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [skus, setSkus] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: '' });
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showTaskTypeModal, setShowTaskTypeModal] = useState(false);
  const [showSkuModal, setShowSkuModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [showTaskStatusModal, setShowTaskStatusModal] = useState(false);
  const [editingTaskStatus, setEditingTaskStatus] = useState(null);
  const [taskStatusForm, setTaskStatusForm] = useState({ value: '', label: '', color: 'slate', icon: 'Circle', order: 99, is_terminal: false });
  const [savingTaskStatus, setSavingTaskStatus] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editingTaskType, setEditingTaskType] = useState(null);
  const [editingSku, setEditingSku] = useState(null);
  const [editingAssignee, setEditingAssignee] = useState(null);
  const [newBrandName, setNewBrandName] = useState('');
  const [newBrandIcon, setNewBrandIcon] = useState('');
  const [newBrandManagerEmail, setNewBrandManagerEmail] = useState('');
  const [newBrandStoreUrls, setNewBrandStoreUrls] = useState({});
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [newTaskTypeKind, setNewTaskTypeKind] = useState('design'); // 'design' | 'content'
  const [newTaskTypeMediaType, setNewTaskTypeMediaType] = useState('static');
  const [newTaskTypeDimensions, setNewTaskTypeDimensions] = useState('');
  const [newTaskTypePlatformDimensions, setNewTaskTypePlatformDimensions] = useState({});
  const [newTaskTypeGuidelines, setNewTaskTypeGuidelines] = useState('');
  const [taskTypeGroupExpanded, setTaskTypeGroupExpanded] = useState({ static: false, video: false });
  const [newProductName, setNewProductName] = useState('');
  const [newProductBrandId, setNewProductBrandId] = useState('');
  const [newProductType, setNewProductType] = useState('Single');
  const [newProductEan, setNewProductEan] = useState('');
  // SKU DNA fields (prices, dimensions, weight)
  const [productMrp, setProductMrp] = useState('');
  const [productBauPrice, setProductBauPrice] = useState('');
  const [productDealPrice, setProductDealPrice] = useState('');
  const [productExclusiveDealPrice, setProductExclusiveDealPrice] = useState('');
  const [productLengthCm, setProductLengthCm] = useState('');
  const [productWidthCm, setProductWidthCm] = useState('');
  const [productHeightCm, setProductHeightCm] = useState('');
  const [productWeightValue, setProductWeightValue] = useState('');
  const [productWeightUnit, setProductWeightUnit] = useState('g');
  const [dnaSectionOpen, setDnaSectionOpen] = useState(false);

  // DNA CSV bulk-update states
  const [showDnaCsvModal, setShowDnaCsvModal] = useState(false);
  const [dnaCsvFile, setDnaCsvFile] = useState(null);
  const [dnaCsvPreview, setDnaCsvPreview] = useState([]);
  const [dnaImportResults, setDnaImportResults] = useState(null);
  const [isDnaUploading, setIsDnaUploading] = useState(false);

  const [newAssigneeName, setNewAssigneeName] = useState('');
  const [newAssigneeEmail, setNewAssigneeEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // CSV upload states
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Image CSV upload states
  const [showImageCsvModal, setShowImageCsvModal] = useState(false);
  const [imageCsvFile, setImageCsvFile] = useState(null);
  const [imageCsvPreview, setImageCsvPreview] = useState([]);
  const [imageImportResults, setImageImportResults] = useState(null);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  
  // Platform Elements states
  const [platformElements, setPlatformElements] = useState({});
  const [selectedElementPlatform, setSelectedElementPlatform] = useState('Amazon');
  const [showElementModal, setShowElementModal] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [newElementKey, setNewElementKey] = useState('');
  const [newElementName, setNewElementName] = useState('');
  const [newElementWeight, setNewElementWeight] = useState('');
  const [showElementCsvModal, setShowElementCsvModal] = useState(false);
  const [elementCsvFile, setElementCsvFile] = useState(null);
  const [elementCsvPreview, setElementCsvPreview] = useState([]);
  const [elementImportResults, setElementImportResults] = useState(null);
  
  // Platform Icons states
  const [platformIcons, setPlatformIcons] = useState({});
  const [platforms, setPlatforms] = useState([]);
  const [showIconModal, setShowIconModal] = useState(false);
  const [editingIconPlatform, setEditingIconPlatform] = useState(null);
  const [newIconUrl, setNewIconUrl] = useState('');
  const [showAddPlatformModal, setShowAddPlatformModal] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState('');
  const [newPlatformIcon, setNewPlatformIcon] = useState('');
  
  // Product Platform URLs state
  const [productPlatformUrls, setProductPlatformUrls] = useState({});
  
  // Product Image URL state
  const [productImageUrl, setProductImageUrl] = useState('');
  
  // Collapsible brand groups
  const [expandedBrands, setExpandedBrands] = useState({});

  // Group SKUs by brand
  const skusByBrand = useMemo(() => {
    const grouped = {};
    brands.forEach(brand => {
      grouped[brand.id] = {
        brand,
        products: skus.filter(sku => sku.brand_id === brand.id)
      };
    });
    return grouped;
  }, [brands, skus]);

  const toggleBrandExpand = (brandId) => {
    setExpandedBrands(prev => ({
      ...prev,
      [brandId]: !prev[brandId]
    }));
  };

  const expandAllBrands = () => {
    const allExpanded = {};
    brands.forEach(b => { allExpanded[b.id] = true; });
    setExpandedBrands(allExpanded);
  };

  const collapseAllBrands = () => {
    setExpandedBrands({});
  };

  // Export data as CSV
  const exportToCSV = (data, filename, columns) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create CSV header
    const headers = columns.map(col => col.label).join(',');
    
    // Create CSV rows
    const rows = data.map(item => {
      return columns.map(col => {
        let value = col.getValue ? col.getValue(item) : item[col.key];
        // Handle null/undefined
        if (value === null || value === undefined) value = '';
        // Escape quotes and wrap in quotes if contains comma or newline
        value = String(value);
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });

    // Combine headers and rows
    const csvContent = [headers, ...rows].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${data.length} records to ${filename}.csv`);
  };

  // Export functions for each data type
  const exportBrands = () => {
    exportToCSV(brands, 'brands', [
      { key: 'name', label: 'Brand Name' },
      { key: 'manager_email', label: 'Manager Email' },
      { key: 'icon_url', label: 'Icon URL' },
      { key: 'created_at', label: 'Created At' }
    ]);
  };

  const exportSKUs = () => {
    // Export in same format as import template
    const platformColumns = ['Amazon', 'Flipkart', 'Offline', 'Blinkit', 'Zepto', 'Wellversed.in', 'Instamart', 'Meesho'];
    
    const columns = [
      { label: 'brand_name', getValue: (item) => brands.find(b => b.id === item.brand_id)?.name || '' },
      { key: 'product_name', label: 'product_name' },
      { key: 'product_type', label: 'product_type', getValue: (item) => item.product_type || 'Single' },
      { key: 'ean', label: 'ean', getValue: (item) => item.ean || '' },
      ...platformColumns.map(platform => ({
        label: `${platform.toLowerCase().replace('.', '')}_url`,
        getValue: (item) => item.platform_urls?.[platform] || ''
      })),
      { key: 'image_url', label: 'image_url', getValue: (item) => item.image_url || '' }
    ];
    
    exportToCSV(skus, 'products_export', columns);
  };

  const exportTaskTypes = () => {
    exportToCSV(taskTypes, 'task_types', [
      { key: 'name', label: 'Task Type Name' },
      { label: 'Platform Dimensions', getValue: (item) => {
        if (!item.platform_dimensions) return '';
        return Object.entries(item.platform_dimensions).map(([platform, dims]) => `${platform}: ${dims}`).join(' | ');
      }},
      { key: 'created_at', label: 'Created At' }
    ]);
  };

  const exportAssignees = () => {
    exportToCSV(assignees, 'assignees', [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'created_at', label: 'Created At' }
    ]);
  };

  const exportPlatformElements = () => {
    // Flatten platform elements for export
    const flattenedElements = [];
    Object.entries(platformElements).forEach(([platform, elements]) => {
      Object.entries(elements).forEach(([key, elem]) => {
        flattenedElements.push({
          platform,
          key,
          name: elem.name,
          weight: elem.weight
        });
      });
    });
    
    exportToCSV(flattenedElements, 'platform_elements', [
      { key: 'platform', label: 'Platform' },
      { key: 'key', label: 'Element Key' },
      { key: 'name', label: 'Element Name' },
      { key: 'weight', label: 'Weight (%)' }
    ]);
  };

  const exportPlatforms = () => {
    const platformData = platforms.map(p => {
      const platformName = typeof p === 'string' ? p : p.name;
      return {
        name: platformName,
        icon_url: platformIcons[platformName] || (p.icon_url || '')
      };
    });
    
    exportToCSV(platformData, 'platforms', [
      { key: 'name', label: 'Platform Name' },
      { key: 'icon_url', label: 'Icon URL' }
    ]);
  };

  useEffect(() => {
    // Check for Gmail connection callback
    if (searchParams.get('gmail') === 'connected') {
      toast.success('Gmail connected successfully!');
    }
    
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [brandsRes, typesRes, skusRes, assigneesRes, gmailRes, elementsRes, iconsRes, platformsRes, statusesRes] = await Promise.all([
        fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/task-types`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/skus`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/assignees`, { credentials: 'include' }),
        fetch(`${API_URL}/api/gmail/status`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platform-elements`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platform-icons`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platforms`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/task-statuses`, { credentials: 'include' }),
      ]);
      
      if (brandsRes.ok) setBrands(await brandsRes.json());
      if (typesRes.ok) setTaskTypes(await typesRes.json());
      if (skusRes.ok) setSkus(await skusRes.json());
      if (assigneesRes.ok) setAssignees(await assigneesRes.json());
      if (gmailRes.ok) setGmailStatus(await gmailRes.json());
      if (elementsRes.ok) setPlatformElements(await elementsRes.json());
      if (iconsRes.ok) setPlatformIcons(await iconsRes.json());
      if (platformsRes.ok) setPlatforms(await platformsRes.json());
      if (statusesRes.ok) setTaskStatuses(await statusesRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Platform Elements CRUD
  const handleSaveElement = async () => {
    if (!newElementName.trim() || !newElementWeight) return;
    setIsSubmitting(true);
    
    try {
      if (editingElement) {
        // Update existing element
        const response = await fetch(`${API_URL}/api/platform-elements/${selectedElementPlatform}/${editingElement.key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: newElementName,
            weight: parseInt(newElementWeight)
          })
        });

        if (!response.ok) {
          const errorData = await safeJsonParse(response);
          throw new Error(errorData.detail || 'Failed to update element');
        }

        toast.success('Element updated successfully');
      } else {
        // Create new element
        if (!newElementKey.trim()) return;
        const key = newElementKey.toLowerCase().replace(/\s+/g, '_');
        const response = await fetch(`${API_URL}/api/platform-elements`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            platform: selectedElementPlatform,
            key,
            name: newElementName,
            weight: parseInt(newElementWeight)
          })
        });

        if (!response.ok) {
          const errorData = await safeJsonParse(response);
          throw new Error(errorData.detail || 'Failed to save element');
        }

        toast.success('Element added successfully');
      }
      
      fetchData();
      closeElementModal();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditElement = (key, elem) => {
    setEditingElement({ key, ...elem });
    setNewElementKey(key);
    setNewElementName(elem.name);
    setNewElementWeight(elem.weight.toString());
    setShowElementModal(true);
  };

  const handleDeleteElement = async (platform, key) => {
    if (!window.confirm('Are you sure you want to delete this element?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/platform-elements/${platform}/${key}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete element');
      
      toast.success('Element deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete element');
    }
  };

  const closeElementModal = () => {
    setShowElementModal(false);
    setEditingElement(null);
    setNewElementKey('');
    setNewElementName('');
    setNewElementWeight('');
  };

  const handleElementCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setElementCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const parsed = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, i) => { row[h] = values[i] || ''; });
        return row;
      }).filter(row => row.key && row.name);
      
      setElementCsvPreview(parsed);
    };
    reader.readAsText(file);
  };

  const handleElementBulkImport = async () => {
    if (elementCsvPreview.length === 0) return;
    setIsUploading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/platform-elements/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          platform: selectedElementPlatform,
          elements: elementCsvPreview.map(row => ({
            key: row.key,
            name: row.name,
            weight: parseInt(row.weight) || 0
          }))
        })
      });

      if (!response.ok) throw new Error('Failed to import elements');
      
      const result = await response.json();
      setElementImportResults(result);
      toast.success(`Imported ${result.imported} elements`);
      fetchData();
    } catch (error) {
      toast.error('Failed to import elements');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadElementTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform-elements/sample-csv/${selectedElementPlatform}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedElementPlatform.toLowerCase()}_elements_template.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Brand CRUD
  const handleSaveBrand = async () => {
    if (!newBrandName.trim()) return;
    setIsSubmitting(true);
    
    try {
      const url = editingBrand 
        ? `${API_URL}/api/masters/brands/${editingBrand.id}`
        : `${API_URL}/api/masters/brands`;
      
      const response = await fetch(url, {
        method: editingBrand ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          id: editingBrand?.id, 
          name: newBrandName, 
          icon_url: newBrandIcon, 
          manager_email: newBrandManagerEmail,
          store_urls: newBrandStoreUrls
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to save brand');
      }

      toast.success(editingBrand ? 'Brand updated successfully' : 'Brand added successfully');
      fetchData();
      closeBrandModal();
    } catch (error) {
      toast.error(error.message || 'Failed to save brand');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/masters/brands/${brandId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to delete brand');
      }

      toast.success('Brand deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete brand');
    }
  };

  const closeBrandModal = () => {
    setShowBrandModal(false);
    setEditingBrand(null);
    setNewBrandName('');
    setNewBrandIcon('');
    setNewBrandManagerEmail('');
    setNewBrandStoreUrls({});
  };

  const openEditBrand = (brand) => {
    setEditingBrand(brand);
    setNewBrandName(brand.name);
    setNewBrandIcon(brand.icon_url || '');
    setNewBrandManagerEmail(brand.manager_email || '');
    setNewBrandStoreUrls(brand.store_urls || {});
    setShowBrandModal(true);
  };

  // Task Type CRUD
  const handleSaveTaskType = async () => {
    if (!newTaskTypeName.trim()) return;
    setIsSubmitting(true);
    
    try {
      const url = editingTaskType 
        ? `${API_URL}/api/masters/task-types/${editingTaskType.id}`
        : `${API_URL}/api/masters/task-types`;
      
      const response = await fetch(url, {
        method: editingTaskType ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          id: editingTaskType?.id, 
          name: newTaskTypeName,
          kind: newTaskTypeKind,
          media_type: newTaskTypeKind !== 'design' ? 'static' : newTaskTypeMediaType,
          dimensions: newTaskTypeKind === 'design' ? (newTaskTypeDimensions || null) : null,
          platform_dimensions: newTaskTypeKind === 'design' ? newTaskTypePlatformDimensions : {},
          guidelines: newTaskTypeKind !== 'design' ? (newTaskTypeGuidelines || null) : null,
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to save task type');
      }

      toast.success(editingTaskType ? 'Task type updated successfully' : 'Task type added successfully');
      fetchData();
      closeTaskTypeModal();
    } catch (error) {
      toast.error(error.message || 'Failed to save task type');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTaskType = async (taskTypeId) => {
    if (!window.confirm('Are you sure you want to delete this task type?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/masters/task-types/${taskTypeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to delete task type');
      }

      toast.success('Task type deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete task type');
    }
  };

  const closeTaskTypeModal = () => {
    setShowTaskTypeModal(false);
    setEditingTaskType(null);
    setNewTaskTypeName('');
    setNewTaskTypeKind('design');
    setNewTaskTypeMediaType('static');
    setNewTaskTypeDimensions('');
    setNewTaskTypePlatformDimensions({});
    setNewTaskTypeGuidelines('');
  };

  const openEditTaskType = (taskType) => {
    setEditingTaskType(taskType);
    setNewTaskTypeName(taskType.name);
    setNewTaskTypeKind(taskType.kind || 'design');
    setNewTaskTypeMediaType(taskType.media_type || 'static');
    setNewTaskTypeDimensions(taskType.dimensions || '');
    setNewTaskTypePlatformDimensions(taskType.platform_dimensions || {});
    setNewTaskTypeGuidelines(taskType.guidelines || '');
    setShowTaskTypeModal(true);
  };


  // Task Log Status CRUD
  const handleSaveTaskStatus = async () => {
    const label = (taskStatusForm.label || '').trim();
    if (!label) {
      toast.error('Label is required');
      return;
    }
    let value = (taskStatusForm.value || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (!value) {
      toast.error('Value is required');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(value)) {
      toast.error('Value must be snake_case (a-z, 0-9, _)');
      return;
    }
    setSavingTaskStatus(true);
    try {
      const url = editingTaskStatus
        ? `${API_URL}/api/masters/task-statuses/${editingTaskStatus.id}`
        : `${API_URL}/api/masters/task-statuses`;
      const method = editingTaskStatus ? 'PUT' : 'POST';
      const body = {
        ...(editingTaskStatus || {}),
        value,
        label,
        color: taskStatusForm.color,
        icon: taskStatusForm.icon,
        order: Number(taskStatusForm.order) || 1,
        is_terminal: !!taskStatusForm.is_terminal,
      };
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
      if (!res.ok) {
        const j = await res.json();
        throw new Error(j.detail || 'Save failed');
      }
      invalidateStatusCache();
      const updated = await fetch(`${API_URL}/api/masters/task-statuses`, { credentials: 'include' });
      if (updated.ok) setTaskStatuses(await updated.json());
      toast.success(editingTaskStatus ? 'Status updated' : 'Status created');
      setShowTaskStatusModal(false);
      setEditingTaskStatus(null);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSavingTaskStatus(false);
    }
  };

  const deleteTaskStatus = async (s) => {
    if (s.is_built_in) {
      toast.error('Built-in statuses cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete "${s.label}"? Existing tasks with this status will be moved back to Pending.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/masters/task-statuses/${s.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        const j = await res.json();
        throw new Error(j.detail || 'Delete failed');
      }
      invalidateStatusCache();
      const updated = await fetch(`${API_URL}/api/masters/task-statuses`, { credentials: 'include' });
      if (updated.ok) setTaskStatuses(await updated.json());
      toast.success(`Deleted "${s.label}"`);
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };


  // Assignee CRUD
  const handleSaveAssignee = async () => {
    if (!newAssigneeName.trim()) return;
    setIsSubmitting(true);
    
    try {
      const url = editingAssignee 
        ? `${API_URL}/api/masters/assignees/${editingAssignee.id}`
        : `${API_URL}/api/masters/assignees`;
      
      const response = await fetch(url, {
        method: editingAssignee ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: editingAssignee?.id, name: newAssigneeName, email: newAssigneeEmail })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to save assignee');
      }

      toast.success(editingAssignee ? 'Assignee updated successfully' : 'Assignee added successfully');
      fetchData();
      closeAssigneeModal();
    } catch (error) {
      toast.error(error.message || 'Failed to save assignee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAssignee = async (assigneeId) => {
    if (!window.confirm('Are you sure you want to delete this assignee?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/masters/assignees/${assigneeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to delete assignee');
      }

      toast.success('Assignee deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete assignee');
    }
  };

  const closeAssigneeModal = () => {
    setShowAssigneeModal(false);
    setEditingAssignee(null);
    setNewAssigneeName('');
    setNewAssigneeEmail('');
  };

  const openEditAssignee = (assignee) => {
    setEditingAssignee(assignee);
    setNewAssigneeName(assignee.name);
    setNewAssigneeEmail(assignee.email || '');
    setShowAssigneeModal(true);
  };

  // Product CRUD
  const handleSaveSku = async () => {
    if (!newProductName.trim() || !newProductBrandId) return;
    setIsSubmitting(true);
    
    try {
      const url = editingSku 
        ? `${API_URL}/api/masters/skus/${editingSku.id}`
        : `${API_URL}/api/masters/skus`;
      
      const response = await fetch(url, {
        method: editingSku ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          product_name: newProductName,
          brand_id: newProductBrandId,
          product_type: newProductType,
          ean: newProductEan || null,
          platform_urls: productPlatformUrls,
          image_url: productImageUrl || null,
          mrp: productMrp === '' ? null : parseInt(productMrp, 10),
          bau_price: productBauPrice === '' ? null : parseInt(productBauPrice, 10),
          deal_price: productDealPrice === '' ? null : parseInt(productDealPrice, 10),
          exclusive_deal_price: productExclusiveDealPrice === '' ? null : parseInt(productExclusiveDealPrice, 10),
          length_cm: productLengthCm === '' ? null : parseFloat(productLengthCm),
          width_cm: productWidthCm === '' ? null : parseFloat(productWidthCm),
          height_cm: productHeightCm === '' ? null : parseFloat(productHeightCm),
          weight_value: productWeightValue === '' ? null : parseFloat(productWeightValue),
          weight_unit: productWeightValue === '' ? null : productWeightUnit,
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to save product');
      }

      toast.success(editingSku ? 'Product updated successfully' : 'Product added successfully');
      fetchData();
      closeSkuModal();
    } catch (error) {
      toast.error(error.message || 'Failed to save product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSku = async (skuId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/masters/skus/${skuId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to delete product');
      }

      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete product');
    }
  };

  const closeSkuModal = () => {
    setShowSkuModal(false);
    setEditingSku(null);
    setNewProductName('');
    setNewProductBrandId('');
    setNewProductType('Single');
    setNewProductEan('');
    setProductPlatformUrls({});
    setProductImageUrl('');
    setProductMrp('');
    setProductBauPrice('');
    setProductDealPrice('');
    setProductExclusiveDealPrice('');
    setProductLengthCm('');
    setProductWidthCm('');
    setProductHeightCm('');
    setProductWeightValue('');
    setProductWeightUnit('g');
    setDnaSectionOpen(false);
  };

  const openEditSku = (sku) => {
    setEditingSku(sku);
    setNewProductName(sku.product_name);
    setNewProductBrandId(sku.brand_id);
    setNewProductType(sku.product_type || 'Single');
    setNewProductEan(sku.ean || '');
    setProductPlatformUrls(sku.platform_urls || {});
    setProductImageUrl(sku.image_url || '');
    setProductMrp(sku.mrp ?? '');
    setProductBauPrice(sku.bau_price ?? '');
    setProductDealPrice(sku.deal_price ?? '');
    setProductExclusiveDealPrice(sku.exclusive_deal_price ?? '');
    setProductLengthCm(sku.length_cm ?? '');
    setProductWidthCm(sku.width_cm ?? '');
    setProductHeightCm(sku.height_cm ?? '');
    setProductWeightValue(sku.weight_value ?? '');
    setProductWeightUnit(sku.weight_unit || 'g');
    setDnaSectionOpen(false);
    setShowSkuModal(true);
  };

  // Fetch product image from platform URL
  // Platform Icon CRUD
  const handleSaveIcon = async () => {
    if (!editingIconPlatform) return;
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/platform-icons`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          platform: editingIconPlatform,
          icon_url: newIconUrl
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to save icon');
      }

      toast.success('Icon updated successfully');
      fetchData();
      closeIconModal();
    } catch (error) {
      toast.error(error.message || 'Failed to save icon');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetIconToDefault = async (platform) => {
    try {
      const response = await fetch(`${API_URL}/api/platform-icons/${platform}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to reset icon');
      }

      toast.success('Icon reset to default');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to reset icon');
    }
  };

  const closeIconModal = () => {
    setShowIconModal(false);
    setEditingIconPlatform(null);
    setNewIconUrl('');
  };

  const openEditIcon = (platform) => {
    setEditingIconPlatform(platform);
    setNewIconUrl(platformIcons[platform] || '');
    setShowIconModal(true);
  };

  // Add Platform
  const handleAddPlatform = async () => {
    if (!newPlatformName.trim()) return;
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/platforms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newPlatformName.trim(),
          icon_url: newPlatformIcon || ''
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to add platform');
      }

      toast.success('Platform added successfully');
      fetchData();
      setShowAddPlatformModal(false);
      setNewPlatformName('');
      setNewPlatformIcon('');
    } catch (error) {
      toast.error(error.message || 'Failed to add platform');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete Platform
  const handleDeletePlatform = async (platformName) => {
    if (!window.confirm(`Delete platform "${platformName}"? This cannot be undone.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/platforms/${encodeURIComponent(platformName)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to delete platform');
      }

      toast.success('Platform deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete platform');
    }
  };

  // CSV Upload Functions
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setCsvFile(file);
    setImportResults(null);
    
    // Platform list for URL parsing
    const platforms = ["Amazon", "Flipkart", "Offline", "Blinkit", "Zepto", "Wellversed.in", "Instamart", "Meesho"];
    
    // Preview CSV content
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Validate headers
      if (!headers.includes('brand_name') || !headers.includes('product_name')) {
        toast.error('CSV must have "brand_name" and "product_name" columns');
        setCsvFile(null);
        setCsvPreview([]);
        return;
      }
      
      const brandIdx = headers.indexOf('brand_name');
      const productIdx = headers.indexOf('product_name');
      const productTypeIdx = headers.indexOf('product_type');
      const eanIdx = headers.indexOf('ean');
      
      // Get platform URL column indices
      const platformUrlIndices = {};
      platforms.forEach(p => {
        const urlKey = `${p.toLowerCase()}_url`;
        const idx = headers.indexOf(urlKey);
        if (idx !== -1) {
          platformUrlIndices[p] = idx;
        }
      });
      
      const preview = lines.slice(1, 11).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const platformUrls = {};
        Object.entries(platformUrlIndices).forEach(([platform, idx]) => {
          if (values[idx]) {
            platformUrls[platform] = values[idx];
          }
        });
        return {
          brand_name: values[brandIdx] || '',
          product_name: values[productIdx] || '',
          product_type: productTypeIdx !== -1 ? (values[productTypeIdx] || 'Single') : 'Single',
          ean: eanIdx !== -1 ? (values[eanIdx] || '') : '',
          platform_urls: platformUrls,
          has_urls: Object.keys(platformUrls).length > 0
        };
      });
      
      setCsvPreview(preview);
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    setIsUploading(true);
    
    // Platform list for URL parsing
    const platforms = ["Amazon", "Flipkart", "Offline", "Blinkit", "Zepto", "Wellversed.in", "Instamart", "Meesho"];
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const brandIdx = headers.indexOf('brand_name');
        const productIdx = headers.indexOf('product_name');
        const productTypeIdx = headers.indexOf('product_type');
        const eanIdx = headers.indexOf('ean');
        
        // Get platform URL column indices
        const platformUrlIndices = {};
        platforms.forEach(p => {
          const urlKey = `${p.toLowerCase()}_url`;
          const idx = headers.indexOf(urlKey);
          if (idx !== -1) {
            platformUrlIndices[p] = idx;
          }
        });
        
        const products = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const platformUrls = {};
          Object.entries(platformUrlIndices).forEach(([platform, idx]) => {
            if (values[idx]) {
              platformUrls[platform] = values[idx];
            }
          });
          return {
            brand_name: values[brandIdx] || '',
            product_name: values[productIdx] || '',
            product_type: productTypeIdx !== -1 ? (values[productTypeIdx] || 'Single') : 'Single',
            ean: eanIdx !== -1 ? (values[eanIdx] || '') : '',
            platform_urls: platformUrls
          };
        }).filter(p => p.brand_name && p.product_name);
        
        const response = await fetch(`${API_URL}/api/masters/skus/bulk-import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ products })
        });
        
        const results = await safeJsonParse(response);
        
        if (!response.ok) {
          throw new Error(results.detail || 'Failed to import products');
        }
        
        setImportResults(results);
        
        if (results.imported > 0) {
          toast.success(`Successfully imported ${results.imported} products`);
        }
        if (results.updated > 0) {
          toast.success(`Successfully updated ${results.updated} products with URLs`);
        }
        if (results.skipped > 0) {
          toast.warning(`${results.skipped} products skipped`);
        }
        
        fetchData();
        setIsUploading(false);
      };
      reader.readAsText(csvFile);
    } catch (error) {
      toast.error(error.message || 'Failed to import products');
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/masters/skus/sample-csv`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to download template');
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products_template.csv');
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup after a delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const closeCsvModal = () => {
    setShowCsvModal(false);
    setCsvFile(null);
    setCsvPreview([]);
    setImportResults(null);
  };

  // Image CSV Upload Functions
  const handleImageCsvSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setImageCsvFile(file);
    
    // Read and preview CSV
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      
      if (!headers.includes('product_name') || !headers.includes('image_url')) {
        toast.error('CSV must have "product_name" and "image_url" columns');
        setImageCsvFile(null);
        return;
      }
      
      // Parse preview rows
      const previewData = [];
      for (let i = 1; i < Math.min(lines.length, 6); i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });
        previewData.push(row);
      }
      
      setImageCsvPreview(previewData);
      setShowImageCsvModal(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImageCsvUpload = async () => {
    if (!imageCsvFile) return;
    
    setIsUploadingImages(true);
    try {
      const formData = new FormData();
      formData.append('file', imageCsvFile);
      
      const response = await fetch(`${API_URL}/api/masters/skus/import-images`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await safeJsonParse(response);
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to import images');
      }
      
      setImageImportResults(data);
      fetchData();
      toast.success(`Updated ${data.updated || 0} product images`);
    } catch (error) {
      toast.error(error.message || 'Failed to import images');
    } finally {
      setIsUploadingImages(false);
    }
  };

  const closeImageCsvModal = () => {
    setShowImageCsvModal(false);
    setImageCsvFile(null);
    setImageCsvPreview([]);
    setImageImportResults(null);
  };

  const handleDownloadImageTemplate = () => {
    // Create CSV content with existing products
    const headers = ['product_name', 'brand_name', 'image_url'];
    const rows = skus.map(sku => [
      `"${sku.product_name || ''}"`,
      `"${sku.brand_name || ''}"`,
      `"${sku.image_url || ''}"`
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'product_images_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded with existing products');
  };

  // SKU DNA Bulk Update Handlers
  const handleDownloadDnaTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/masters/skus/dna-sample-csv`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sku_dna_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded with all existing products');
    } catch (e) {
      toast.error(e.message || 'Failed to download template');
    }
  };

  const handleDnaCsvSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDnaCsvFile(file);
    setDnaImportResults(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) {
        toast.error('CSV is empty');
        return;
      }
      const parseRow = (row) => {
        const result = [];
        let cur = ''; let inQ = false;
        for (let i = 0; i < row.length; i++) {
          const ch = row[i];
          if (ch === '"') inQ = !inQ;
          else if (ch === ',' && !inQ) { result.push(cur); cur = ''; }
          else cur += ch;
        }
        result.push(cur);
        return result.map(s => s.trim().replace(/^"|"$/g, ''));
      };
      const headers = parseRow(lines[0]).map(h => h.toLowerCase().trim());
      const pIdx = headers.indexOf('product_name');
      if (pIdx === -1) {
        toast.error('CSV must include a "product_name" column');
        setDnaCsvFile(null);
        return;
      }
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const v = parseRow(lines[i]);
        const row = {};
        headers.forEach((h, idx) => { row[h] = v[idx] || ''; });
        if (row.product_name) rows.push(row);
      }
      setDnaCsvPreview(rows);
      setShowDnaCsvModal(true);
    };
    reader.readAsText(file);
    // Reset file input so same file can be re-selected
    e.target.value = '';
  };

  const closeDnaCsvModal = () => {
    setShowDnaCsvModal(false);
    setDnaCsvFile(null);
    setDnaCsvPreview([]);
    setDnaImportResults(null);
  };

  const handleDnaBulkUpload = async () => {
    if (!dnaCsvPreview.length) return;
    setIsDnaUploading(true);
    try {
      const toNum = (v) => (v === '' || v == null ? null : Number(v));
      const rows = dnaCsvPreview.map(r => ({
        brand_name: r.brand_name || null,
        product_name: r.product_name,
        ean: r.ean || null,
        mrp: toNum(r.mrp),
        bau_price: toNum(r.bau_price),
        deal_price: toNum(r.deal_price),
        exclusive_deal_price: toNum(r.exclusive_deal_price),
        length_cm: toNum(r.length_cm),
        width_cm: toNum(r.width_cm),
        height_cm: toNum(r.height_cm),
        weight_value: toNum(r.weight_value),
        weight_unit: r.weight_unit || null,
      }));
      const res = await fetch(`${API_URL}/api/masters/skus/dna-bulk-update`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      if (!res.ok) throw new Error('Failed to upload');
      const result = await res.json();
      setDnaImportResults(result);
      toast.success(`${result.updated} product(s) updated · ${result.skipped} skipped`);
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Bulk update failed');
    } finally {
      setIsDnaUploading(false);
    }
  };

  // Gmail Integration
  const handleConnectGmail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gmail/auth-url`, {
        credentials: 'include'
      });
      
      const data = await safeJsonParse(response);
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to initiate Gmail connection');
      }

      window.location.href = data.auth_url;
    } catch (error) {
      toast.error(error.message || 'Failed to initiate Gmail connection');
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail?')) return;
    
    try {
      await fetch(`${API_URL}/api/gmail/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Gmail disconnected');
      setGmailStatus({ connected: false, email: '' });
    } catch (error) {
      toast.error('Failed to disconnect Gmail');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EB4AC]"></div>
      </div>
    );
  }

  return (
    <div className={cn("mx-auto space-y-8 animate-fade-in", settingsTab === 'usage' ? 'max-w-7xl' : 'max-w-5xl')} data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage master data and integrations</p>
      </div>

      <Tabs value={settingsTab} onValueChange={setSettingsTab} className="space-y-6">
        <TabsList className="bg-slate-100 h-auto max-w-full flex-nowrap overflow-x-auto justify-start [&_button]:px-2.5 [&_button]:text-[13px] [&_button]:whitespace-nowrap">
          <TabsTrigger value="brands" data-testid="brands-tab">Brands</TabsTrigger>
          <TabsTrigger value="tasktypes" data-testid="tasktypes-tab">Task Types</TabsTrigger>
          <TabsTrigger value="taskstatuses" data-testid="taskstatuses-tab">Task Log Status</TabsTrigger>
          <TabsTrigger value="assignees" data-testid="assignees-tab">Assignees</TabsTrigger>
          <TabsTrigger value="skus" data-testid="skus-tab">Products</TabsTrigger>
          <TabsTrigger value="elements" data-testid="elements-tab">Platform Elements</TabsTrigger>
          <TabsTrigger value="icons" data-testid="icons-tab">Platform Icons</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="integrations-tab">Integrations</TabsTrigger>
          <TabsTrigger value="eanowners" data-testid="ean-owners-tab">EAN Owners</TabsTrigger>
          <TabsTrigger value="usage" data-testid="usage-analytics-tab-trigger">Usage Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="eanowners">
          <EanOwnersTab />
        </TabsContent>

        <TabsContent value="usage">
          <UsageAnalyticsTab />
        </TabsContent>

        {/* Brands Tab */}
        <TabsContent value="brands" className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Brands</CardTitle>
                <CardDescription>Manage brand options for task submissions</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={exportBrands}
                  className="border-slate-300"
                  data-testid="export-brands-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button
                  onClick={() => setShowBrandModal(true)}
                  className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                  data-testid="add-brand-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Brand
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {brands.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No brands added yet</p>
                ) : (
                  brands.map((brand) => (
                    <div
                      key={brand.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50"
                    >
                      <div>
                        <span className="font-medium text-slate-900">{brand.name}</span>
                        {brand.manager_email && (
                          <span className="ml-2 text-sm text-slate-500">({brand.manager_email})</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditBrand(brand)}
                          className="text-slate-500 hover:text-[#2EB4AC]"
                          data-testid={`edit-brand-${brand.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBrand(brand.id)}
                          className="text-slate-500 hover:text-red-500"
                          data-testid={`delete-brand-${brand.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Task Types Tab */}
        <TabsContent value="tasktypes" className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Task Types</CardTitle>
                <CardDescription>Manage task type options for submissions</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={exportTaskTypes}
                  className="border-slate-300"
                  data-testid="export-task-types-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button
                  onClick={() => setShowTaskTypeModal(true)}
                  className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                  data-testid="add-task-type-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {taskTypes.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No task types added yet</p>
                ) : (
                  <>
                    {/* ============ DESIGN KIND ============ */}
                    {(() => {
                      const designTypes = taskTypes.filter(t => (t.kind || 'design') === 'design');
                      if (designTypes.length === 0) return null;
                      const staticTypes = designTypes.filter(t => t.media_type !== 'video');
                      const videoTypes  = designTypes.filter(t => t.media_type === 'video');
                      return (
                        <div data-testid="task-types-kind-design">
                          {/* Kind heading */}
                          <div className="flex items-center gap-2 mb-3">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-sm font-bold bg-violet-100 text-violet-700">🎨</span>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">Design</h3>
                            <span className="text-[11px] text-slate-400">· {designTypes.length} type{designTypes.length !== 1 ? 's' : ''}</span>
                            <div className="flex-1 h-px bg-slate-200 ml-2" />
                          </div>

                          <div className="space-y-3">
                            {/* Static sub-group */}
                            {staticTypes.length > 0 && (
                              <div className="border border-blue-200 rounded-lg overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setTaskTypeGroupExpanded(prev => ({ ...prev, static: !prev.static }))}
                                  className="w-full px-4 py-3 bg-blue-50 flex items-center justify-between hover:bg-blue-100 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <Badge className="bg-blue-100 text-blue-700">Static</Badge>
                                    <span className="text-sm text-slate-600">{staticTypes.length} task type{staticTypes.length !== 1 ? 's' : ''}</span>
                                  </div>
                                  <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${taskTypeGroupExpanded.static ? 'rotate-180' : ''}`} />
                                </button>
                                {taskTypeGroupExpanded.static && (
                                  <div className="p-3 space-y-2 bg-white">
                                    {staticTypes.map((taskType) => (
                                      <div key={taskType.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                                        <div className="flex items-center gap-3">
                                          <span className="font-medium text-slate-900">{taskType.name}</span>
                                          {Object.keys(taskType.platform_dimensions || {}).length > 0 ? (
                                            <Badge variant="secondary" className="bg-[#2EB4AC]/10 text-[#2EB4AC] text-xs">Dimensions mapped</Badge>
                                          ) : (
                                            <Badge variant="secondary" className="bg-slate-100 text-slate-500 text-xs">No dimensions</Badge>
                                          )}
                                        </div>
                                        <div className="flex gap-2">
                                          <Button variant="ghost" size="sm" onClick={() => openEditTaskType(taskType)} className="text-slate-500 hover:text-[#2EB4AC]"><Pencil className="h-4 w-4" /></Button>
                                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTaskType(taskType.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Video sub-group */}
                            {videoTypes.length > 0 && (
                              <div className="border border-purple-200 rounded-lg overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setTaskTypeGroupExpanded(prev => ({ ...prev, video: !prev.video }))}
                                  className="w-full px-4 py-3 bg-purple-50 flex items-center justify-between hover:bg-purple-100 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <Badge className="bg-purple-100 text-purple-700">Video</Badge>
                                    <span className="text-sm text-slate-600">{videoTypes.length} task type{videoTypes.length !== 1 ? 's' : ''}</span>
                                  </div>
                                  <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${taskTypeGroupExpanded.video ? 'rotate-180' : ''}`} />
                                </button>
                                {taskTypeGroupExpanded.video && (
                                  <div className="p-3 space-y-2 bg-white">
                                    {videoTypes.map((taskType) => (
                                      <div key={taskType.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                                        <div className="flex items-center gap-3">
                                          <span className="font-medium text-slate-900">{taskType.name}</span>
                                          {Object.keys(taskType.platform_dimensions || {}).length > 0 ? (
                                            <Badge variant="secondary" className="bg-[#2EB4AC]/10 text-[#2EB4AC] text-xs">Dimensions mapped</Badge>
                                          ) : (
                                            <Badge variant="secondary" className="bg-slate-100 text-slate-500 text-xs">No dimensions</Badge>
                                          )}
                                        </div>
                                        <div className="flex gap-2">
                                          <Button variant="ghost" size="sm" onClick={() => openEditTaskType(taskType)} className="text-slate-500 hover:text-[#2EB4AC]"><Pencil className="h-4 w-4" /></Button>
                                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTaskType(taskType.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ============ CONTENT KIND ============ */}
                    {(() => {
                      const contentTypes = taskTypes.filter(t => t.kind === 'content');
                      if (contentTypes.length === 0) return null;
                      return (
                        <div data-testid="task-types-kind-content">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-sm font-bold bg-emerald-100 text-emerald-700">✍</span>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">Content</h3>
                            <span className="text-[11px] text-slate-400">· {contentTypes.length} type{contentTypes.length !== 1 ? 's' : ''}</span>
                            <div className="flex-1 h-px bg-slate-200 ml-2" />
                          </div>
                          <div className="border border-emerald-200 rounded-lg overflow-hidden">
                            <div className="p-3 space-y-2 bg-white">
                              {contentTypes.map((taskType) => (
                                <div key={taskType.id} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-slate-900">{taskType.name}</span>
                                      {taskType.guidelines ? (
                                        <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 text-[10px]">Guidelines set</Badge>
                                      ) : (
                                        <Badge variant="secondary" className="bg-slate-100 text-slate-500 text-[10px]">No guidelines</Badge>
                                      )}
                                    </div>
                                    {taskType.guidelines && (
                                      <p className="text-[11px] text-slate-500 line-clamp-2 whitespace-pre-wrap">{taskType.guidelines}</p>
                                    )}
                                  </div>
                                  <div className="flex gap-2 shrink-0">
                                    <Button variant="ghost" size="sm" onClick={() => openEditTaskType(taskType)} className="text-slate-500 hover:text-[#2EB4AC]"><Pencil className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTaskType(taskType.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* ============ AUDIO KIND ============ */}
                    {(() => {
                      const audioTypes = taskTypes.filter(t => t.kind === 'audio');
                      return (
                        <div data-testid="task-types-kind-audio">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-sm font-bold bg-orange-100 text-orange-700">🎧</span>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">Audio</h3>
                            <span className="text-[11px] text-slate-400">· {audioTypes.length} type{audioTypes.length !== 1 ? 's' : ''}</span>
                            <div className="flex-1 h-px bg-slate-200 ml-2" />
                          </div>
                          <div className="border border-orange-200 rounded-lg overflow-hidden">
                            <div className="p-3 space-y-2 bg-white">
                              {audioTypes.length === 0 && (
                                <p className="p-3 text-sm text-slate-400">No audio task types yet — use "Add Task Type" and pick <b>Audio</b>.</p>
                              )}
                              {audioTypes.map((taskType) => (
                                <div key={taskType.id} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`audio-task-type-${taskType.id}`}>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-slate-900">{taskType.name}</span>
                                    {taskType.guidelines && <p className="text-[11px] text-slate-500 line-clamp-2 whitespace-pre-wrap mt-1">{taskType.guidelines}</p>}
                                  </div>
                                  <div className="flex gap-2 shrink-0">
                                    <Button variant="ghost" size="sm" onClick={() => openEditTaskType(taskType)} className="text-slate-500 hover:text-[#2EB4AC]"><Pencil className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTaskType(taskType.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Task Log Status Tab */}
        <TabsContent value="taskstatuses" className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Task Log Status</CardTitle>
                <CardDescription>
                  Control the status options shown in Task Log & Brand Workspace. Built-in statuses can be restyled or renamed but not deleted.
                </CardDescription>
              </div>
              <Button
                onClick={() => { setEditingTaskStatus(null); setTaskStatusForm({ value: '', label: '', color: 'slate', icon: 'Circle', order: (taskStatuses.length || 0) + 1, is_terminal: false }); setShowTaskStatusModal(true); }}
                className="bg-[#2EB4AC] hover:bg-[#249a93] text-white"
                data-testid="add-task-status-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Status
              </Button>
            </CardHeader>
            <CardContent>
              {taskStatuses.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">No task statuses configured yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="task-statuses-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
                        <th className="text-left py-2 px-3 w-16">Order</th>
                        <th className="text-left py-2 px-3">Preview</th>
                        <th className="text-left py-2 px-3">Label</th>
                        <th className="text-left py-2 px-3">Internal value</th>
                        <th className="text-left py-2 px-3 w-32">Terminal</th>
                        <th className="text-left py-2 px-3 w-32">Type</th>
                        <th className="text-right py-2 px-3 w-32">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskStatuses.sort((a, b) => (a.order || 0) - (b.order || 0)).map(s => {
                        const colorMap = STATUS_COLOR_MAP[s.color] || STATUS_COLOR_MAP.slate;
                        const IconComp = STATUS_ICONS[s.icon] || STATUS_ICONS.Circle;
                        return (
                          <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`task-status-row-${s.value}`}>
                            <td className="py-2 px-3 text-slate-500">{s.order || '—'}</td>
                            <td className="py-2 px-3">
                              <Badge className={cn('border', colorMap.pill)}>
                                <IconComp className="h-3 w-3 mr-1" />
                                {s.label}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 font-medium text-slate-900">{s.label}</td>
                            <td className="py-2 px-3 text-slate-500 font-mono text-xs">{s.value}</td>
                            <td className="py-2 px-3">
                              {s.is_terminal ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Yes</Badge>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3">
                              {s.is_built_in ? (
                                <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">Built-in</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">Custom</Badge>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => { setEditingTaskStatus(s); setTaskStatusForm({ value: s.value, label: s.label, color: s.color, icon: s.icon, order: s.order, is_terminal: !!s.is_terminal }); setShowTaskStatusModal(true); }}
                                className="h-7 px-2"
                                data-testid={`edit-task-status-${s.value}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => deleteTaskStatus(s)}
                                disabled={s.is_built_in}
                                className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                                title={s.is_built_in ? 'Built-in statuses cannot be deleted (rename or restyle instead)' : 'Delete'}
                                data-testid={`delete-task-status-${s.value}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[11px] text-slate-400 italic mt-3">
                Tip: "Terminal" statuses (Completed / Defunct) automatically stamp the <code>completed_at</code> timestamp when a task transitions to them.
              </p>
            </CardContent>
          </Card>
        </TabsContent>



        {/* Assignees Tab */}
        <TabsContent value="assignees" className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Assignees</CardTitle>
                <CardDescription>Manage design team members who can be assigned tasks</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={exportAssignees}
                  className="border-slate-300"
                  data-testid="export-assignees-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button
                  onClick={() => setShowAssigneeModal(true)}
                  className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                  data-testid="add-assignee-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Assignee
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {assignees.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No assignees added yet</p>
                ) : (
                  assignees.map((assignee) => (
                    <div
                      key={assignee.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50"
                    >
                      <div>
                        <span className="font-medium text-slate-900">{assignee.name}</span>
                        {assignee.email && (
                          <span className="ml-2 text-sm text-slate-500">({assignee.email})</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditAssignee(assignee)}
                          className="text-slate-500 hover:text-[#2EB4AC]"
                          data-testid={`edit-assignee-${assignee.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAssignee(assignee.id)}
                          className="text-slate-500 hover:text-red-500"
                          data-testid={`delete-assignee-${assignee.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="skus" className="space-y-6">
          {/* Bulk Import Cards - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* CSV Upload Card */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#2EB4AC]/10 flex items-center justify-center">
                    <FileSpreadsheet className="h-5 w-5 text-[#2EB4AC]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Bulk Import Products</CardTitle>
                    <CardDescription>Upload CSV to add products</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="border-[#2EB4AC] text-[#2EB4AC] hover:bg-[#2EB4AC]/10"
                    data-testid="download-products-template-btn"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Template
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowCsvModal(true)}
                    className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                    data-testid="upload-csv-btn"
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Import Images Card */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Image className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Bulk Import Images</CardTitle>
                    <CardDescription>Upload CSV with image URLs</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadImageTemplate}
                    className="border-purple-500 text-purple-600 hover:bg-purple-50"
                    data-testid="download-images-template-btn"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Template
                  </Button>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleImageCsvSelect}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      data-testid="upload-images-csv-input"
                    />
                    <Button
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white pointer-events-none"
                      data-testid="upload-images-csv-btn"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Update SKU DNA Card */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                    <Dna className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Bulk Update SKU DNA</CardTitle>
                    <CardDescription>Prices, dimensions, weight via CSV</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadDnaTemplate}
                    className="border-violet-500 text-violet-600 hover:bg-violet-50"
                    data-testid="download-dna-template-btn"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Template
                  </Button>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleDnaCsvSelect}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      data-testid="upload-dna-csv-input"
                    />
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white pointer-events-none"
                      data-testid="upload-dna-csv-btn"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Products by Brand</CardTitle>
                <CardDescription>
                  {skus.length} products across {brands.length} brands
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={expandAllBrands}
                  className="text-slate-500 hover:text-[#2EB4AC]"
                >
                  Expand All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={collapseAllBrands}
                  className="text-slate-500 hover:text-[#2EB4AC]"
                >
                  Collapse All
                </Button>
                <Button
                  variant="outline"
                  onClick={exportSKUs}
                  className="border-slate-300"
                  data-testid="export-skus-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button
                  onClick={() => setShowSkuModal(true)}
                  className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                  data-testid="add-sku-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {brands.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No brands available</p>
                  <p className="text-sm text-slate-400 mt-1">Add brands first in Master Data tab</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {brands.map((brand) => {
                    const brandProducts = skusByBrand[brand.id]?.products || [];
                    const isExpanded = expandedBrands[brand.id];
                    
                    return (
                      <Collapsible
                        key={brand.id}
                        open={isExpanded}
                        onOpenChange={() => toggleBrandExpand(brand.id)}
                      >
                        <CollapsibleTrigger asChild>
                          <div 
                            className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                            data-testid={`brand-group-${brand.id}`}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                              )}
                              <span className="font-medium text-slate-900">{brand.name}</span>
                              <Badge variant="secondary" className="bg-slate-100">
                                {brandProducts.length} products
                              </Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNewProductBrandId(brand.id);
                                setShowSkuModal(true);
                              }}
                              className="text-[#2EB4AC] hover:bg-[#2EB4AC]/10"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="ml-7 mt-1 space-y-1 border-l-2 border-slate-100 pl-4">
                            {brandProducts.length === 0 ? (
                              <p className="text-sm text-slate-400 py-2">No products for this brand</p>
                            ) : (
                              brandProducts.map((sku) => (
                                <div
                                  key={sku.id}
                                  className="flex items-center justify-between p-2 rounded hover:bg-slate-50"
                                >
                                  <div className="flex items-center gap-3">
                                    {sku.image_url ? (
                                      <img 
                                        src={sku.image_url} 
                                        alt={sku.product_name} 
                                        className="w-8 h-8 rounded object-cover border border-slate-200"
                                      />
                                    ) : (
                                      <div className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
                                        <Package className="h-4 w-4 text-slate-400" />
                                      </div>
                                    )}
                                    <span className="text-slate-700">{sku.product_name}</span>
                                    {/* Product Type Badge */}
                                    {sku.product_type && sku.product_type !== 'Single' && (
                                      <Badge 
                                        variant="secondary" 
                                        className={`text-[10px] px-1.5 py-0 h-5 ${
                                          sku.product_type === 'Combo' 
                                            ? 'bg-amber-100 text-amber-700 border-amber-200' 
                                            : 'bg-purple-100 text-purple-700 border-purple-200'
                                        }`}
                                      >
                                        {sku.product_type}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEditSku(sku)}
                                      className="h-7 w-7 p-0 text-slate-400 hover:text-[#2EB4AC]"
                                      data-testid={`edit-sku-${sku.id}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteSku(sku.id)}
                                      className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                                      data-testid={`delete-sku-${sku.id}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform Elements Tab */}
        <TabsContent value="elements" className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Layers className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <CardTitle className="text-lg">Platform Elements</CardTitle>
                  <CardDescription>
                    Configure tracking elements for Listing Quality Control per platform
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selectedElementPlatform} onValueChange={setSelectedElementPlatform}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {platforms.map(p => (
                      <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={() => setShowElementModal(true)}
                  className="bg-[#2EB4AC] hover:bg-[#25908A]"
                  data-testid="add-element-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Element
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowElementCsvModal(true)}
                  data-testid="import-elements-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadElementTemplate}
                  data-testid="download-element-template-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
                <Button
                  variant="outline"
                  onClick={exportPlatformElements}
                  className="border-slate-300"
                  data-testid="export-elements-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export All
                </Button>
              </div>

              {/* Elements List */}
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-200">
                {platformElements[selectedElementPlatform] && Object.entries(platformElements[selectedElementPlatform]).length > 0 ? (
                  Object.entries(platformElements[selectedElementPlatform]).map(([key, elem]) => {
                    const platformData = platforms.find(p => p.name === selectedElementPlatform);
                    return (
                    <div key={key} className="flex items-center justify-between p-4 hover:bg-slate-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          {/* Platform Icon */}
                          {platformData?.icon_url ? (
                            <img src={platformData.icon_url} alt={selectedElementPlatform} className="w-5 h-5 object-contain" />
                          ) : (
                            <div className="w-5 h-5 rounded bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-500">
                              {selectedElementPlatform?.charAt(0)}
                            </div>
                          )}
                          <span className="font-medium text-slate-900">{elem.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            Weight: {elem.weight}%
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 ml-8">Key: {key}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditElement(key, elem)}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-[#2EB4AC]"
                          data-testid={`edit-element-${key}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteElement(selectedElementPlatform, key)}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                          data-testid={`delete-element-${key}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )})
                ) : (
                  <div className="p-8 text-center">
                    <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No elements configured for {selectedElementPlatform}</p>
                    <p className="text-sm text-slate-400 mt-1">Add elements to start tracking listing quality</p>
                  </div>
                )}
              </div>

              {/* Total Weight Display */}
              {platformElements[selectedElementPlatform] && Object.keys(platformElements[selectedElementPlatform]).length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Total Weight:</span>
                    <span className={`font-bold ${
                      Object.values(platformElements[selectedElementPlatform]).reduce((sum, e) => sum + e.weight, 0) === 100 
                        ? 'text-green-600' 
                        : 'text-orange-600'
                    }`}>
                      {Object.values(platformElements[selectedElementPlatform]).reduce((sum, e) => sum + e.weight, 0)}%
                      {Object.values(platformElements[selectedElementPlatform]).reduce((sum, e) => sum + e.weight, 0) !== 100 && (
                        <span className="text-xs font-normal text-slate-500 ml-2">(should be 100%)</span>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform Icons Tab */}
        <TabsContent value="icons" className="space-y-6">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="h-5 w-5 text-[#2EB4AC]" />
                    Platforms & Icons
                  </CardTitle>
                  <CardDescription>
                    Manage platforms and their icons. Changes apply everywhere across the app.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={exportPlatforms}
                    className="border-slate-300"
                    data-testid="export-platforms-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <Button
                    onClick={() => setShowAddPlatformModal(true)}
                    className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Platform
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {platforms.map((platform) => (
                  <div 
                    key={platform.name}
                    className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                        {platform.icon_url ? (
                          <img 
                            src={platform.icon_url} 
                            alt={platform.name} 
                            className="w-8 h-8 object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <Globe className="h-6 w-6 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{platform.name}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[150px]">
                          {platform.icon_url ? 'Has icon' : 'No icon'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditIcon(platform.name)}
                        className="text-slate-500 hover:text-[#2EB4AC]"
                        title="Edit Icon"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePlatform(platform.name)}
                        className="text-slate-500 hover:text-red-500"
                        title="Delete Platform"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">
                  <strong>Tip:</strong> Use icon URLs from{' '}
                  <a href="https://simpleicons.org" target="_blank" rel="noopener noreferrer" className="text-[#2EB4AC] hover:underline">
                    Simple Icons
                  </a>{' '}or company favicons. Deleting a platform removes it from all dropdowns.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          <MetaAdAccountsCard />
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <CardTitle className="text-lg">Gmail Integration</CardTitle>
                  <CardDescription>
                    Connect your Gmail account to send task notification emails
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3">
                  {gmailStatus.connected ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      <div>
                        <p className="font-medium text-slate-900">Connected</p>
                        <p className="text-sm text-slate-500">{gmailStatus.email}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900">Not Connected</p>
                        <p className="text-sm text-slate-500">
                          Connect Gmail to enable email notifications
                        </p>
                      </div>
                    </>
                  )}
                </div>
                {gmailStatus.connected ? (
                  <Button
                    variant="outline"
                    onClick={handleDisconnectGmail}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="disconnect-gmail-btn"
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    onClick={handleConnectGmail}
                    className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                    data-testid="connect-gmail-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Connect Gmail
                  </Button>
                )}
              </div>
              
              <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Gmail OAuth credentials must be configured in the 
                  environment for this feature to work. Contact your administrator if you 
                  encounter issues connecting.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Brand Modal */}
      <Dialog open={showBrandModal} onOpenChange={closeBrandModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingBrand ? 'Edit Brand' : 'Add New Brand'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="brandName" className="text-slate-700 font-medium">
                Brand Name *
              </Label>
              <Input
                id="brandName"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                placeholder="Enter brand name"
                className="mt-2 h-11"
                data-testid="brand-name-input"
              />
            </div>
            <div>
              <Label htmlFor="brandIcon" className="text-slate-700 font-medium">
                Icon URL (optional)
              </Label>
              <Input
                id="brandIcon"
                value={newBrandIcon}
                onChange={(e) => setNewBrandIcon(e.target.value)}
                placeholder="https://example.com/brand-logo.png"
                className="mt-2 h-11"
              />
              {newBrandIcon && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Preview:</span>
                  <img src={newBrandIcon} alt="" className="w-6 h-6 object-contain" onError={(e) => e.target.style.display='none'} />
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="brandManagerEmail" className="text-slate-700 font-medium">
                Brand Manager Email *
              </Label>
              <Input
                id="brandManagerEmail"
                type="email"
                value={newBrandManagerEmail}
                onChange={(e) => setNewBrandManagerEmail(e.target.value)}
                placeholder="manager@company.com"
                className="mt-2 h-11"
                data-testid="brand-manager-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">This email will be auto-filled as notification recipient when this brand is selected</p>
            </div>
            
            {/* Brand Store URLs by Platform */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
                <Globe className="h-4 w-4" />
                Brand Store URLs (by Platform)
              </Label>
              <p className="text-xs text-slate-500 mb-3">
                Add your brand store page URL for each platform
              </p>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {Object.keys(platformIcons).length > 0 ? (
                  Object.keys(platformIcons).map(platform => (
                    <div key={platform} className="flex items-center gap-2">
                      <div className="w-28 flex items-center gap-2 flex-shrink-0">
                        {platformIcons[platform] ? (
                          <img src={platformIcons[platform]} alt={platform} className="w-5 h-5 object-contain" />
                        ) : (
                          <span className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500">
                            {platform.charAt(0)}
                          </span>
                        )}
                        <span className="text-sm text-slate-600 truncate">{platform}</span>
                      </div>
                      <Input
                        placeholder={`https://www.${platform.toLowerCase().replace(/\s/g, '')}.com/store/...`}
                        value={newBrandStoreUrls[platform] || ''}
                        onChange={(e) => setNewBrandStoreUrls(prev => ({
                          ...prev,
                          [platform]: e.target.value
                        }))}
                        className="h-9 text-sm flex-1"
                      />
                      {newBrandStoreUrls[platform] && (
                        <a 
                          href={newBrandStoreUrls[platform]} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#2EB4AC] hover:text-[#25908A]"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No platforms configured. Add platforms in the Platform Icons tab first.
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeBrandModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveBrand}
              disabled={isSubmitting || !newBrandName.trim()}
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
              data-testid="save-brand-btn"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Type Modal */}
      <Dialog open={showTaskTypeModal} onOpenChange={closeTaskTypeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTaskType ? 'Edit Task Type' : 'Add New Task Type'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Kind selector — Design vs Content vs Audio */}
            <div>
              <Label className="text-slate-700 font-medium">Task Kind *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setNewTaskTypeKind('design')}
                  className={`relative text-left p-3 rounded-lg border-2 transition-all ${newTaskTypeKind === 'design' ? 'border-violet-500 bg-violet-50/60' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                  data-testid="task-kind-design-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded text-[11px] font-bold bg-violet-100 text-violet-700">🎨</span>
                    <span className="font-semibold text-slate-900">Design</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug">Uses dimensions & media type. Shown on Design submission flow.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setNewTaskTypeKind('content')}
                  className={`relative text-left p-3 rounded-lg border-2 transition-all ${newTaskTypeKind === 'content' ? 'border-emerald-500 bg-emerald-50/60' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                  data-testid="task-kind-content-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded text-[11px] font-bold bg-emerald-100 text-emerald-700">✍</span>
                    <span className="font-semibold text-slate-900">Content</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug">Uses editorial guidelines. Shown on Content submission flow.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setNewTaskTypeKind('audio')}
                  className={`relative text-left p-3 rounded-lg border-2 transition-all ${newTaskTypeKind === 'audio' ? 'border-orange-500 bg-orange-50/60' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                  data-testid="task-kind-audio-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded text-[11px] font-bold bg-orange-100 text-orange-700">🎧</span>
                    <span className="font-semibold text-slate-900">Audio</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug">Voiceovers, jingles, sound. Shown on Audio submission flow.</p>
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="taskTypeName" className="text-slate-700 font-medium">
                Task Type Name *
              </Label>
              <Input
                id="taskTypeName"
                value={newTaskTypeName}
                onChange={(e) => setNewTaskTypeName(e.target.value)}
                placeholder={newTaskTypeKind === 'content' ? 'e.g., Blog Post, Email Copy' : newTaskTypeKind === 'audio' ? 'e.g., Voiceover, Jingle' : 'e.g., Banner, Listing Images'}
                className="mt-2 h-11"
                data-testid="task-type-name-input"
              />
            </div>

            {/* Media Type — design only */}
            {newTaskTypeKind === 'design' && (
              <div>
                <Label className="text-slate-700 font-medium">
                  Media Type *
                </Label>
                <Select value={newTaskTypeMediaType} onValueChange={setNewTaskTypeMediaType}>
                  <SelectTrigger className="mt-2 h-11" data-testid="media-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="static">Static (Image)</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Platform-Specific Dimensions — design only */}
            {newTaskTypeKind === 'design' && (
              <div>
                <Label className="text-slate-700 font-medium">
                  Platform Dimensions
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-3">
                  Add dimensions for each platform. When a platform is selected in task submission, these dimensions will auto-populate.
                </p>
                <div className="space-y-3 max-h-[300px] overflow-y-auto border rounded-lg p-3 bg-slate-50">
                  {platforms.map(platform => {
                    const platformName = typeof platform === 'string' ? platform : platform.name;
                    return (
                      <div key={platformName} className="flex items-start gap-3">
                        <div className="w-24 flex-shrink-0 pt-2">
                          <span className="text-sm font-medium text-slate-700">{platformName}</span>
                        </div>
                        <Textarea
                          value={newTaskTypePlatformDimensions[platformName] || ''}
                          onChange={(e) => setNewTaskTypePlatformDimensions(prev => ({
                            ...prev,
                            [platformName]: e.target.value
                          }))}
                          placeholder={`e.g., 1500x1500 px\n970x600 px`}
                          className="min-h-[60px] resize-none text-sm flex-1"
                          data-testid={`platform-dimension-${platformName}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Guidelines — content only */}
            {newTaskTypeKind !== 'design' && (
              <div>
                <Label className="text-slate-700 font-medium">
                  Editorial Guidelines
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Tone, word-count range, brand voice rules, sentence-length caps, etc. These will be shown to the submitter when this task type is selected.
                </p>
                <Textarea
                  value={newTaskTypeGuidelines}
                  onChange={(e) => setNewTaskTypeGuidelines(e.target.value)}
                  placeholder={'e.g., 600-800 words. Tone: confident, friendly, science-backed.\nAvoid: medical claims, "best in market" superlatives.\nMust include: 1 product mention per 200 words, 2 internal links.'}
                  className="min-h-[140px] text-sm"
                  data-testid="task-type-guidelines-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeTaskTypeModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTaskType}
              disabled={isSubmitting || !newTaskTypeName.trim()}
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
              data-testid="save-task-type-btn"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Task Log Status Modal */}
      <Dialog open={showTaskStatusModal} onOpenChange={setShowTaskStatusModal}>
        <DialogContent className="max-w-lg" data-testid="task-status-modal">
          <DialogHeader>
            <DialogTitle>{editingTaskStatus ? `Edit "${editingTaskStatus.label}"` : 'Add Task Status'}</DialogTitle>
            <DialogDescription>
              These statuses appear in the Task Log and Brand Workspace status pickers.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {/* Live preview */}
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-2">
              <span className="text-xs text-slate-500">Preview:</span>
              {(() => {
                const colorMap = STATUS_COLOR_MAP[taskStatusForm.color] || STATUS_COLOR_MAP.slate;
                const IconComp = STATUS_ICONS[taskStatusForm.icon] || STATUS_ICONS.Circle;
                return (
                  <Badge className={cn('border', colorMap.pill)}>
                    <IconComp className="h-3 w-3 mr-1" />
                    {taskStatusForm.label || 'Label'}
                  </Badge>
                );
              })()}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Display Label *</Label>
                <Input
                  value={taskStatusForm.label}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTaskStatusForm(prev => ({
                      ...prev,
                      label: v,
                      // Auto-fill internal value while creating
                      value: editingTaskStatus ? prev.value : v.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
                    }));
                  }}
                  placeholder="e.g. Blocked, Review, QA"
                  className="mt-1"
                  data-testid="task-status-label-input"
                />
              </div>
              <div>
                <Label className="text-xs">Internal Value *</Label>
                <Input
                  value={taskStatusForm.value}
                  onChange={(e) => setTaskStatusForm({ ...taskStatusForm, value: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })}
                  placeholder="e.g. blocked"
                  disabled={editingTaskStatus?.is_built_in}
                  className="mt-1 font-mono text-xs"
                  data-testid="task-status-value-input"
                />
                {editingTaskStatus?.is_built_in && (
                  <p className="text-[10px] text-slate-400 mt-1">Locked — built-in statuses use a fixed value.</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Color</Label>
                <Select value={taskStatusForm.color} onValueChange={(v) => setTaskStatusForm({ ...taskStatusForm, color: v })}>
                  <SelectTrigger className="mt-1" data-testid="task-status-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_CHOICES.map(c => (
                      <SelectItem key={c} value={c}>
                        <div className="flex items-center gap-2">
                          <span className={cn('inline-block w-3 h-3 rounded-full', STATUS_COLOR_MAP[c]?.dotBg)} />
                          <span className="capitalize">{c}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Icon</Label>
                <Select value={taskStatusForm.icon} onValueChange={(v) => setTaskStatusForm({ ...taskStatusForm, icon: v })}>
                  <SelectTrigger className="mt-1" data-testid="task-status-icon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_CHOICES.map(name => {
                      const I = STATUS_ICONS[name];
                      return (
                        <SelectItem key={name} value={name}>
                          <div className="flex items-center gap-2">
                            <I className="h-3.5 w-3.5" />
                            {name}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Display Order</Label>
                <Input
                  type="number"
                  min="1"
                  value={taskStatusForm.order}
                  onChange={(e) => setTaskStatusForm({ ...taskStatusForm, order: parseInt(e.target.value || '1', 10) })}
                  className="mt-1"
                  data-testid="task-status-order-input"
                />
              </div>
              <div className="flex items-end pb-1.5">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Switch
                    checked={taskStatusForm.is_terminal}
                    onCheckedChange={(checked) => setTaskStatusForm({ ...taskStatusForm, is_terminal: checked })}
                    data-testid="task-status-terminal-toggle"
                  />
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-700">Terminal status</span>
                    <span className="text-[10px] text-slate-400">Stamps completed_at when reached</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskStatusModal(false)} data-testid="task-status-cancel-btn">Cancel</Button>
            <Button onClick={handleSaveTaskStatus} disabled={savingTaskStatus} className="bg-[#2EB4AC] hover:bg-[#249a93] text-white" data-testid="task-status-save-btn">
              {savingTaskStatus ? 'Saving…' : (editingTaskStatus ? 'Save Changes' : 'Add Status')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Assignee Modal */}
      <Dialog open={showAssigneeModal} onOpenChange={closeAssigneeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAssignee ? 'Edit Assignee' : 'Add New Assignee'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="assigneeName" className="text-slate-700 font-medium">
                Name *
              </Label>
              <Input
                id="assigneeName"
                value={newAssigneeName}
                onChange={(e) => setNewAssigneeName(e.target.value)}
                placeholder="e.g., John Doe"
                className="mt-2 h-11"
                data-testid="assignee-name-input"
              />
            </div>
            <div>
              <Label htmlFor="assigneeEmail" className="text-slate-700 font-medium">
                Email (optional)
              </Label>
              <Input
                id="assigneeEmail"
                type="email"
                value={newAssigneeEmail}
                onChange={(e) => setNewAssigneeEmail(e.target.value)}
                placeholder="e.g., john@company.com"
                className="mt-2 h-11"
                data-testid="assignee-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAssigneeModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignee}
              disabled={isSubmitting || !newAssigneeName.trim()}
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
              data-testid="save-assignee-btn"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showSkuModal} onOpenChange={closeSkuModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSku ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label htmlFor="productName" className="text-slate-700 font-medium">
                Product Name *
              </Label>
              <Input
                id="productName"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder="e.g., Protein Powder 1kg"
                className="mt-2 h-11"
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Brand *</Label>
              <Select value={newProductBrandId} onValueChange={setNewProductBrandId}>
                <SelectTrigger className="mt-2 h-11" data-testid="product-brand-select">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Product Type Selection */}
            <div>
              <Label className="text-slate-700 font-medium">Product Type *</Label>
              <Select value={newProductType} onValueChange={setNewProductType}>
                <SelectTrigger className="mt-2 h-11" data-testid="product-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Single">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      Single Product
                    </div>
                  </SelectItem>
                  <SelectItem value="Combo">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      Combo
                    </div>
                  </SelectItem>
                  <SelectItem value="Multi-Product">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                      Multi-Product
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Categorize as Single, Combo, or Multi-Product SKU
              </p>
            </div>
            
            {/* EAN Number */}
            <div>
              <Label htmlFor="productEan" className="text-slate-700 font-medium">
                EAN Number
              </Label>
              <Input
                id="productEan"
                value={newProductEan}
                onChange={(e) => setNewProductEan(e.target.value)}
                placeholder="e.g., 8901234567890"
                className="mt-2 h-11"
                data-testid="product-ean-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                13-digit barcode number (optional)
              </p>
            </div>
            
            {/* Platform URLs Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
                <Link className="h-4 w-4" />
                Platform Listing URLs
              </Label>
              <p className="text-xs text-slate-500 mb-3">
                Add URLs to link this product to its listings on different platforms
              </p>
              <div className="space-y-3">
                {platforms.map((platform) => (
                  <div key={platform.name} className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded flex-shrink-0">
                      {platform.icon_url ? (
                        <img 
                          src={platform.icon_url} 
                          alt={platform.name} 
                          className="w-5 h-5 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Globe className="h-4 w-4 text-slate-400" />
                      )}
                    </div>
                    <Input
                      placeholder={`${platform.name} listing URL`}
                      value={productPlatformUrls[platform.name] || ''}
                      onChange={(e) => setProductPlatformUrls(prev => ({
                        ...prev,
                        [platform.name]: e.target.value
                      }))}
                      className="h-9 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Product Image URL */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
                <Image className="h-4 w-4" />
                Product Image
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                Paste a direct image URL (right-click product image → Copy image address)
              </p>
              <div className="flex items-center gap-2">
                {productImageUrl && (
                  <img 
                    src={productImageUrl} 
                    alt="Product preview" 
                    className="w-12 h-12 rounded object-cover border border-slate-200 flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <Input
                  placeholder="https://m.media-amazon.com/images/I/..."
                  value={productImageUrl || ''}
                  onChange={(e) => setProductImageUrl(e.target.value)}
                  className="h-9 text-sm flex-1"
                />
              </div>
            </div>

            {/* SKU DNA Section (collapsible) */}
            <div className="rounded-lg border border-violet-200 bg-violet-50/30 overflow-hidden">
              <button
                type="button"
                onClick={() => setDnaSectionOpen(!dnaSectionOpen)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-violet-50 transition-colors"
                data-testid="dna-section-toggle"
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                    <Dna className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-violet-900">SKU DNA</p>
                    <p className="text-[11px] text-violet-600">Prices (₹), dimensions (cm), weight</p>
                  </div>
                </div>
                <ChevronRight className={`h-4 w-4 text-violet-500 transition-transform ${dnaSectionOpen ? 'rotate-90' : ''}`} />
              </button>
              {dnaSectionOpen && (
                <div className="px-4 pb-4 pt-1 space-y-4 border-t border-violet-200">
                  {/* Prices */}
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">Prices (INR, whole numbers)</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-[11px] text-slate-600">MRP</Label>
                        <Input type="number" min="0" step="1" value={productMrp} onChange={(e) => setProductMrp(e.target.value)} placeholder="e.g. 1499" className="h-9 text-sm mt-1" data-testid="dna-mrp" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-600">BAU Price</Label>
                        <Input type="number" min="0" step="1" value={productBauPrice} onChange={(e) => setProductBauPrice(e.target.value)} placeholder="e.g. 1299" className="h-9 text-sm mt-1" data-testid="dna-bau" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-600">Deal Price</Label>
                        <Input type="number" min="0" step="1" value={productDealPrice} onChange={(e) => setProductDealPrice(e.target.value)} placeholder="e.g. 999" className="h-9 text-sm mt-1" data-testid="dna-deal" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-600">Exclusive Deal</Label>
                        <Input type="number" min="0" step="1" value={productExclusiveDealPrice} onChange={(e) => setProductExclusiveDealPrice(e.target.value)} placeholder="e.g. 899" className="h-9 text-sm mt-1" data-testid="dna-excl" />
                      </div>
                    </div>
                  </div>
                  {/* Dimensions */}
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">Dimensions (cm)</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[11px] text-slate-600">Length</Label>
                        <Input type="number" min="0" step="0.1" value={productLengthCm} onChange={(e) => setProductLengthCm(e.target.value)} placeholder="L" className="h-9 text-sm mt-1" data-testid="dna-length" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-600">Width</Label>
                        <Input type="number" min="0" step="0.1" value={productWidthCm} onChange={(e) => setProductWidthCm(e.target.value)} placeholder="W" className="h-9 text-sm mt-1" data-testid="dna-width" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-600">Height</Label>
                        <Input type="number" min="0" step="0.1" value={productHeightCm} onChange={(e) => setProductHeightCm(e.target.value)} placeholder="H" className="h-9 text-sm mt-1" data-testid="dna-height" />
                      </div>
                    </div>
                  </div>
                  {/* Weight */}
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">Weight</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Label className="text-[11px] text-slate-600">Value</Label>
                        <Input type="number" min="0" step="0.1" value={productWeightValue} onChange={(e) => setProductWeightValue(e.target.value)} placeholder="e.g. 210" className="h-9 text-sm mt-1" data-testid="dna-weight-value" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-600">Unit</Label>
                        <Select value={productWeightUnit} onValueChange={setProductWeightUnit}>
                          <SelectTrigger className="h-9 text-sm mt-1" data-testid="dna-weight-unit"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="g">g (grams)</SelectItem>
                            <SelectItem value="kg">kg (kilograms)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeSkuModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSku}
              disabled={isSubmitting || !newProductName.trim() || !newProductBrandId}
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
              data-testid="save-product-btn"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Modal */}
      <Dialog open={showCsvModal} onOpenChange={closeCsvModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Import Products from CSV</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Instructions */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">CSV Format Requirements:</h4>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Required columns: <code className="bg-slate-200 px-1 rounded">brand_name</code> and <code className="bg-slate-200 px-1 rounded">product_name</code></li>
                <li>Optional: <code className="bg-slate-200 px-1 rounded">product_type</code> (Single, Combo, Multi-Product)</li>
                <li>Optional: <code className="bg-slate-200 px-1 rounded">ean</code> (13-digit barcode number)</li>
                <li>Optional URL columns: <code className="bg-slate-200 px-1 rounded">amazon_url</code>, <code className="bg-slate-200 px-1 rounded">flipkart_url</code>, etc.</li>
                <li>Brand names must match existing brands (case-insensitive)</li>
                <li>Existing products will be <strong>updated with URLs</strong> if URL columns are provided</li>
              </ul>
              <Button
                variant="link"
                onClick={handleDownloadTemplate}
                className="text-[#2EB4AC] p-0 h-auto mt-2"
                data-testid="download-csv-template-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                Download sample template (includes URL columns)
              </Button>
            </div>
            
            {/* File Input */}
            <div>
              <Label className="text-slate-700 font-medium">Select CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="mt-2"
                data-testid="csv-file-input"
              />
            </div>
            
            {/* Preview */}
            {csvPreview.length > 0 && (
              <div>
                <Label className="text-slate-700 font-medium">Preview (first 10 rows)</Label>
                <div className="mt-2 border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Brand Name</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Product Name</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Type</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">EAN</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">URLs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 text-slate-700">{row.brand_name}</td>
                          <td className="px-3 py-2 text-slate-700">{row.product_name}</td>
                          <td className="px-3 py-2">
                            <Badge 
                              variant="secondary" 
                              className={`text-[10px] ${
                                row.product_type === 'Combo' 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : row.product_type === 'Multi-Product'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {row.product_type || 'Single'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-slate-500 text-xs font-mono">
                            {row.ean || '—'}
                          </td>
                          <td className="px-3 py-2">
                            {row.has_urls ? (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <Link className="h-3 w-3" />
                                {Object.keys(row.platform_urls).join(', ')}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Import Results */}
            {importResults && (
              <div className={`p-4 rounded-lg border ${(importResults.imported > 0 || importResults.updated > 0) ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <h4 className="font-medium flex items-center gap-2">
                  {(importResults.imported > 0 || importResults.updated > 0) ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                  Import Results
                </h4>
                <div className="mt-2 text-sm space-y-1">
                  {importResults.imported > 0 && (
                    <p className="text-green-700">✓ {importResults.imported} new products imported</p>
                  )}
                  {importResults.updated > 0 && (
                    <p className="text-blue-700">✓ {importResults.updated} existing products updated with URLs</p>
                  )}
                  {importResults.skipped > 0 && (
                    <p className="text-amber-700">⚠ {importResults.skipped} products skipped</p>
                  )}
                </div>
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-slate-600">Errors:</p>
                    <ul className="text-xs text-slate-500 mt-1 max-h-32 overflow-y-auto">
                      {importResults.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                      {importResults.errors.length > 10 && (
                        <li>... and {importResults.errors.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCsvModal}>
              {importResults ? 'Close' : 'Cancel'}
            </Button>
            {!importResults && (
              <Button
                onClick={handleCsvUpload}
                disabled={isUploading || !csvFile}
                className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                data-testid="import-csv-btn"
              >
                {isUploading ? 'Importing...' : 'Import Products'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image CSV Upload Modal */}
      <Dialog open={showImageCsvModal} onOpenChange={closeImageCsvModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5 text-purple-600" />
              Bulk Import Product Images
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Instructions */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-900 mb-2">CSV Format:</h4>
              <ul className="text-sm text-purple-700 space-y-1 list-disc list-inside">
                <li>Required columns: <code className="bg-purple-100 px-1 rounded">product_name</code> and <code className="bg-purple-100 px-1 rounded">image_url</code></li>
                <li>Optional: <code className="bg-purple-100 px-1 rounded">brand_name</code> (helps match if duplicate product names exist)</li>
                <li>Products must already exist in the system</li>
              </ul>
              <p className="text-xs text-purple-600 mt-3">
                Tip: Right-click on product image on Amazon/Flipkart → "Copy image address" to get the URL
              </p>
            </div>
            
            {/* Preview */}
            {imageCsvPreview.length > 0 && !imageImportResults && (
              <div>
                <Label className="text-slate-700 font-medium">Preview (first 5 rows)</Label>
                <div className="mt-2 border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Product Name</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Brand</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Image URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {imageCsvPreview.map((row, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 text-slate-700 truncate max-w-[200px]">{row.product_name}</td>
                          <td className="px-3 py-2 text-slate-500 text-xs">{row.brand_name || '—'}</td>
                          <td className="px-3 py-2">
                            {row.image_url ? (
                              <div className="flex items-center gap-2">
                                <img 
                                  src={row.image_url} 
                                  alt="" 
                                  className="w-8 h-8 rounded object-cover border"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <span className="text-xs text-slate-500 truncate max-w-[150px]">{row.image_url}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Import Results */}
            {imageImportResults && (
              <div className={`p-4 rounded-lg border ${imageImportResults.updated > 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <h4 className="font-medium flex items-center gap-2">
                  {imageImportResults.updated > 0 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                  Import Results
                </h4>
                <div className="mt-2 text-sm space-y-1">
                  {imageImportResults.updated > 0 && (
                    <p className="text-green-700">✓ {imageImportResults.updated} product images updated</p>
                  )}
                  {imageImportResults.not_found_count > 0 && (
                    <div>
                      <p className="text-amber-700">⚠ {imageImportResults.not_found_count} products not found:</p>
                      <ul className="text-xs text-amber-600 mt-1 max-h-24 overflow-y-auto">
                        {imageImportResults.not_found?.map((name, idx) => (
                          <li key={idx}>• {name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImageCsvModal}>
              {imageImportResults ? 'Close' : 'Cancel'}
            </Button>
            {!imageImportResults && (
              <Button
                onClick={handleImageCsvUpload}
                disabled={isUploadingImages || !imageCsvFile}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="import-images-btn"
              >
                {isUploadingImages ? 'Importing...' : 'Import Images'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SKU DNA Bulk Update Modal */}
      <Dialog open={showDnaCsvModal} onOpenChange={closeDnaCsvModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Dna className="h-5 w-5 text-violet-500" />
              Bulk Update SKU DNA
            </DialogTitle>
            <DialogDescription>
              Review the {dnaCsvPreview.length} row(s) detected in your CSV. Only products matching an existing product_name (+ optional brand_name) will be updated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dnaCsvPreview.length > 0 && (
              <div className="max-h-[340px] overflow-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-slate-600">
                      <th className="px-2 py-2">Product</th>
                      <th className="px-2 py-2">Brand</th>
                      <th className="px-2 py-2 text-right">MRP</th>
                      <th className="px-2 py-2 text-right">BAU</th>
                      <th className="px-2 py-2 text-right">Deal</th>
                      <th className="px-2 py-2 text-right">Excl.</th>
                      <th className="px-2 py-2">EAN</th>
                      <th className="px-2 py-2">Dim (L×W×H)</th>
                      <th className="px-2 py-2">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dnaCsvPreview.slice(0, 200).map((r, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 truncate max-w-[180px]">{r.product_name}</td>
                        <td className="px-2 py-1.5 text-slate-500">{r.brand_name || '—'}</td>
                        <td className="px-2 py-1.5 text-right">{r.mrp || '—'}</td>
                        <td className="px-2 py-1.5 text-right">{r.bau_price || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-emerald-700">{r.deal_price || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-violet-700">{r.exclusive_deal_price || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{r.ean || '—'}</td>
                        <td className="px-2 py-1.5 text-[10px]">{r.length_cm || '?'}×{r.width_cm || '?'}×{r.height_cm || '?'}</td>
                        <td className="px-2 py-1.5 text-[10px]">{r.weight_value ? `${r.weight_value} ${r.weight_unit || ''}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {dnaCsvPreview.length > 200 && (
                  <p className="text-[11px] text-slate-400 text-center py-1.5 border-t">Showing first 200 of {dnaCsvPreview.length} rows</p>
                )}
              </div>
            )}
            {dnaImportResults && (
              <div className={`p-3 rounded-lg border text-sm ${dnaImportResults.updated > 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <p className="font-medium flex items-center gap-2">
                  {dnaImportResults.updated > 0 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
                  Bulk Update Results
                </p>
                {dnaImportResults.updated > 0 && <p className="mt-1 text-green-700">✓ {dnaImportResults.updated} product(s) updated</p>}
                {dnaImportResults.skipped > 0 && <p className="text-amber-700">⚠ {dnaImportResults.skipped} row(s) skipped</p>}
                {dnaImportResults.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-slate-600 text-xs">Errors (first 10):</p>
                    <ul className="text-[11px] text-slate-500 mt-1 max-h-24 overflow-y-auto">
                      {dnaImportResults.errors.slice(0, 10).map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDnaCsvModal}>
              {dnaImportResults ? 'Close' : 'Cancel'}
            </Button>
            {!dnaImportResults && dnaCsvPreview.length > 0 && (
              <Button
                onClick={handleDnaBulkUpload}
                disabled={isDnaUploading}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                data-testid="confirm-dna-upload-btn"
              >
                {isDnaUploading ? 'Updating...' : `Update ${dnaCsvPreview.length} SKU(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Element Add Modal */}
      <Dialog open={showElementModal} onOpenChange={closeElementModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingElement ? 'Edit Element' : 'Add New Element'} for {selectedElementPlatform}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-slate-700 font-medium">Element Key</Label>
              <Input
                value={newElementKey}
                onChange={(e) => setNewElementKey(e.target.value)}
                placeholder="e.g., product_title"
                className="mt-1"
                data-testid="element-key-input"
                disabled={!!editingElement}
              />
              <p className="text-xs text-slate-500 mt-1">
                {editingElement ? 'Key cannot be changed' : 'Unique identifier (lowercase, underscores)'}
              </p>
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Element Name</Label>
              <Input
                value={newElementName}
                onChange={(e) => setNewElementName(e.target.value)}
                placeholder="e.g., Product Title"
                className="mt-1"
                data-testid="element-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Weight (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={newElementWeight}
                onChange={(e) => setNewElementWeight(e.target.value)}
                placeholder="e.g., 20"
                className="mt-1"
                data-testid="element-weight-input"
              />
              <p className="text-xs text-slate-500 mt-1">Contribution to quality score (total should be 100%)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeElementModal}>Cancel</Button>
            <Button
              onClick={handleSaveElement}
              disabled={isSubmitting || (!editingElement && !newElementKey) || !newElementName || !newElementWeight}
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
              data-testid="save-element-btn"
            >
              {isSubmitting ? 'Saving...' : (editingElement ? 'Update Element' : 'Add Element')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Element CSV Import Modal */}
      <Dialog open={showElementCsvModal} onOpenChange={() => {
        setShowElementCsvModal(false);
        setElementCsvFile(null);
        setElementCsvPreview([]);
        setElementImportResults(null);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Elements for {selectedElementPlatform}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-[#2EB4AC]" />
                CSV Format Requirements
              </h4>
              <p className="text-sm text-slate-500 mt-2">
                Your CSV file must include columns: <code className="bg-white px-1 rounded">key</code>, 
                <code className="bg-white px-1 rounded">name</code>, <code className="bg-white px-1 rounded">weight</code>
              </p>
              <Button
                variant="link"
                onClick={downloadElementTemplate}
                className="text-[#2EB4AC] p-0 h-auto mt-2"
              >
                <Download className="h-4 w-4 mr-1" />
                Download sample template
              </Button>
            </div>
            
            <div>
              <Label className="text-slate-700 font-medium">Select CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={handleElementCsvUpload}
                className="mt-2"
                data-testid="element-csv-file-input"
              />
            </div>
            
            {elementCsvPreview.length > 0 && (
              <div>
                <Label className="text-slate-700 font-medium">Preview</Label>
                <div className="mt-2 border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Key</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Name</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {elementCsvPreview.map((row, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 text-slate-700">{row.key}</td>
                          <td className="px-3 py-2 text-slate-700">{row.name}</td>
                          <td className="px-3 py-2 text-slate-700">{row.weight}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {elementImportResults && (
              <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                <h4 className="font-medium flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Import Results
                </h4>
                <p className="text-sm text-green-700 mt-2">
                  ✓ {elementImportResults.imported} elements imported
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowElementCsvModal(false);
              setElementCsvFile(null);
              setElementCsvPreview([]);
              setElementImportResults(null);
            }}>
              {elementImportResults ? 'Close' : 'Cancel'}
            </Button>
            {!elementImportResults && (
              <Button
                onClick={handleElementBulkImport}
                disabled={isUploading || elementCsvPreview.length === 0}
                className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
                data-testid="import-elements-csv-btn"
              >
                {isUploading ? 'Importing...' : 'Import Elements'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Platform Icon Edit Modal */}
      <Dialog open={showIconModal} onOpenChange={closeIconModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Edit Icon - {editingIconPlatform}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Current Icon Preview */}
            <div className="flex items-center justify-center p-6 bg-slate-50 rounded-lg border border-slate-200">
              <div className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                {newIconUrl ? (
                  <img 
                    src={newIconUrl} 
                    alt={editingIconPlatform} 
                    className="w-12 h-12 object-contain"
                    onError={(e) => { 
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <Globe className="h-8 w-8 text-slate-400" />
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="iconUrl" className="text-slate-700 font-medium">
                Icon URL
              </Label>
              <Input
                id="iconUrl"
                value={newIconUrl}
                onChange={(e) => setNewIconUrl(e.target.value)}
                placeholder="https://example.com/icon.svg"
                className="mt-2 h-11"
                data-testid="icon-url-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter a URL to an image file (SVG, PNG, or ICO recommended)
              </p>
            </div>
            
            {platformIcons[editingIconPlatform] && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  resetIconToDefault(editingIconPlatform);
                  closeIconModal();
                }}
                className="text-red-500 border-red-200 hover:bg-red-50"
              >
                Reset to Default
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeIconModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveIcon}
              disabled={isSubmitting}
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
              data-testid="save-icon-btn"
            >
              {isSubmitting ? 'Saving...' : 'Save Icon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Platform Modal */}
      <Dialog open={showAddPlatformModal} onOpenChange={() => {
        setShowAddPlatformModal(false);
        setNewPlatformName('');
        setNewPlatformIcon('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add New Platform
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="platformName" className="text-slate-700 font-medium">
                Platform Name *
              </Label>
              <Input
                id="platformName"
                value={newPlatformName}
                onChange={(e) => setNewPlatformName(e.target.value)}
                placeholder="e.g., BigBasket, Nykaa"
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="platformIcon" className="text-slate-700 font-medium">
                Icon URL (optional)
              </Label>
              <Input
                id="platformIcon"
                value={newPlatformIcon}
                onChange={(e) => setNewPlatformIcon(e.target.value)}
                placeholder="https://example.com/icon.png"
                className="mt-2 h-11"
              />
              <p className="text-xs text-slate-500 mt-1">
                You can add or change the icon later from the icons list
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddPlatformModal(false);
              setNewPlatformName('');
              setNewPlatformIcon('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPlatform}
              disabled={isSubmitting || !newPlatformName.trim()}
              className="bg-[#2EB4AC] hover:bg-[#25908A] text-white"
            >
              {isSubmitting ? 'Adding...' : 'Add Platform'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
